"use client";

import {
  JSX,
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { SegmentTabs } from "@/components/motion/segment-tabs";
import { emitModuleInteraction } from "@/lib/module-interaction";
import type { ModuleBeforeAfterSliderData } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import { importsTokenScope } from "./imports-token-scope";
import { KEYBOARD_SETTLE_MS } from "@/config/timing";

const KEYBOARD_STEP = 5;

export default function ModuleBeforeAfterSlider({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleBeforeAfterSliderData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const beforeLabel = data.beforeLabel ?? t("Before");
  const afterLabel = data.afterLabel ?? t("After");
  const caption = data.caption;
  const aspectRatio = data.aspectRatio ?? "16 / 10";
  const defaultPosition = data.defaultPosition ?? 50;
  const beforeSrc = data.beforeSrc;
  const afterSrc = data.afterSrc;
  const beforeAlt = data.beforeAlt ?? beforeLabel;
  const afterAlt = data.afterAlt ?? afterLabel;

  const [pos, setPos] = useState(defaultPosition);
  const [dragging, setDragging] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const posRef = useRef(defaultPosition);
  const dragStartRef = useRef(defaultPosition);
  const pendingRef = useRef<number | null>(null);
  const settleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyPos = (next: number) => {
    posRef.current = next;
    setPos(next);
  };

  const update = (clientX: number) => {
    const el = boxRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    applyPos(Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100)));
  };

  const emitPosition = useCallback((committed: number) => {
    emitModuleInteraction(
      boxRef.current,
      "click",
      { kind: "control", id: "comparison" },
      { value: Math.round(committed) },
    );
  }, []);

  const flushPending = useCallback(() => {
    if (settleRef.current !== null) {
      clearTimeout(settleRef.current);
      settleRef.current = null;
    }
    if (pendingRef.current === null) return;
    const committed = pendingRef.current;
    pendingRef.current = null;
    emitPosition(committed);
  }, [emitPosition]);

  const endDrag = () => {
    setDragging(false);
    if (!dragging) return;
    if (Math.round(posRef.current) === Math.round(dragStartRef.current)) return;
    emitPosition(posRef.current);
  };

  useEffect(
    () => () => {
      if (settleRef.current !== null) clearTimeout(settleRef.current);
    },
    [],
  );

  const presets = [
    { label: beforeLabel, p: 100 },
    { label: t("Split"), p: 50 },
    { label: afterLabel, p: 0 },
  ];

  return (
    <div
      className={cn("w-full", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <div
        ref={boxRef}
        className="relative w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-xl bg-black/[0.03] ring-1 ring-black/[0.1] dark:bg-white/[0.05] dark:ring-white/[0.14]"
        style={{ aspectRatio }}
        onPointerDown={(e: ReactPointerEvent<HTMLDivElement>) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          flushPending();
          dragStartRef.current = posRef.current;
          setDragging(true);
          update(e.clientX);
        }}
        onPointerMove={(e: ReactPointerEvent<HTMLDivElement>) => {
          if (dragging) update(e.clientX);
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <img
          src={beforeSrc}
          alt={beforeAlt}
          className={cn("absolute inset-0 h-full w-full object-cover pointer-events-none", grayscale && "grayscale")}
          draggable={false}
        />
        <div
          className="absolute inset-0"
          style={{
            clipPath: `inset(0 0 0 ${pos}%)`,
            transition: dragging ? "none" : "clip-path 0.4s ease",
          }}
        >
          <img
            src={afterSrc}
            alt={afterAlt}
            className={cn("absolute inset-0 h-full w-full object-cover pointer-events-none", grayscale && "grayscale")}
            draggable={false}
          />
        </div>

        <span
          className="absolute left-3 top-3 rounded-md bg-black/60 px-2 py-1 text-xs text-white/85 backdrop-blur-sm transition-opacity"
          style={{ opacity: pos > 12 ? 1 : 0 }}
        >
          {beforeLabel}
        </span>
        <span
          className="absolute right-3 top-3 rounded-md bg-black/60 px-2 py-1 text-xs text-white backdrop-blur-sm transition-opacity"
          style={{ opacity: pos < 88 ? 1 : 0 }}
        >
          {afterLabel}
        </span>

        <div
          className="absolute inset-y-0 w-px bg-white"
          style={{
            left: `${pos}%`,
            transition: dragging ? "none" : "left 0.4s ease",
          }}
        >
          <button
            type="button"
            role="slider"
            aria-label={t("Move comparison")}
            aria-valuenow={Math.round(pos)}
            aria-valuemin={0}
            aria-valuemax={100}
            onKeyDown={(e) => {
              if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
              const next =
                e.key === "ArrowLeft"
                  ? Math.max(0, posRef.current - KEYBOARD_STEP)
                  : Math.min(100, posRef.current + KEYBOARD_STEP);
              applyPos(next);
              // Arrow keys have no release to hang the event on, so a pause in
              // typing (or focus leaving the handle) ends one adjustment.
              pendingRef.current = next;
              if (settleRef.current !== null) clearTimeout(settleRef.current);
              settleRef.current = setTimeout(flushPending, KEYBOARD_SETTLE_MS);
            }}
            onBlur={flushPending}
            className="absolute top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-full bg-white text-[var(--fg)] dark:text-neutral-900 ring-1 ring-black/[0.2] shadow-sm"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
              <path
                d="M6 4 2 8l4 4M10 4l4 4-4 4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-dashed border-[var(--border-dashed-soft)] pt-4">
        <SegmentTabs
          value={
            presets.some((b) => b.p === Math.round(pos))
              ? String(Math.round(pos))
              : null
          }
          onValueChange={(v) => v !== null && applyPos(Number(v))}
          options={presets.map((b) => ({ value: String(b.p), label: b.label }))}
        />
        {caption && (
          <span
            className={`text-sm text-[var(--subtle)] ${MODULE_LABEL_WRAP.body}`}
          >
            {stripInlineMarkdown(caption)}
          </span>
        )}
      </div>
    </div>
  );
}
